import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Pencil,
  Search,
  ListFilter,
  UserPlus,
  Mail,
  Download,
  Upload,
  Lock,
  LockOpen,
  Trash2,
  UserRoundPlus,
  GraduationCap,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import TabsCst from "@components/TabsCst";
import InputCst from "@components/InputCst/InputCst";
import SortMenuCst from "@components/SortMenuCst";
import FiltersAccordionCst from "@components/FiltersAccordionCst";
import BulkActionBarCst from "@components/BulkActionBarCst";
import { BulkAction } from "@components/BulkActionBarCst/types";
import PaginatorCst from "@components/PaginatorCst";
import UploadModalCst from "@components/UploadModalCst";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import { openToast as showToast } from "@utils/toastStore";
import AsyncProcessBanner from "@components/AsyncProcessBanner";
import {
  openCreatePicker,
  useCanCreate,
} from "@components/CreateOptionPickerCst/host";
import {
  usePaginatedList,
  PaginatedListFilters,
} from "@hooks/usePaginatedList";
import { useAsyncProcess } from "@hooks/useAsyncProcess";
import { useNavbarInitiative } from "@hooks/useNavbarInitiative";
import { useAuth } from "@context/auth";
import { getCookie } from "@utils/utilsCookie";
import { handleSwitchToLearner } from "@utils/utilsCognito";
import { ServicesURL, downloadFile, getQueryString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { AsyncProcessStatusEnum } from "src/interfaces/AsyncProcess";
import { buildUGListUrl, UGTabKey } from "./types";
import { useUsersGroupsPageState } from "./hooks/useUsersGroupsPageState";
import UserItem from "./components/UserItem";
import GroupItem from "./components/GroupItem";
import EnrollLOModal, { EnrollSubjectType } from "./components/EnrollLOModal";
import AddUsersToGroupModal from "./components/AddUsersToGroupModal";

const PAGE_SIZE = 20;

const UsersGroupsV2: React.FC = () => {
  const { t } = useTranslation("users-groups-v2");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "" } = useParams();
  const { user: authUser } = useAuth();
  const { selectedInitiative } = useNavbarInitiative();
  const sessionId = getCookie("sessionId") || "";
  const canCreate = useCanCreate("users-groups");

  const { activeTab, search, fieldFilters, sort, page, setActiveTab, update } =
    useUsersGroupsPageState();

  const isUsersTab = activeTab === "users";

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(
    new Set(),
  );
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  const hasActiveFilters = useMemo(
    () =>
      Object.values(fieldFilters || {}).some((val) => {
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      }),
    [fieldFilters],
  );

  const [isConfirming, setIsConfirming] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [blockUser, setBlockUser] = useState<any>();
  const [bulkStatusTarget, setBulkStatusTarget] = useState<
    "ACTIVE" | "SUSPENDED"
  >();
  const [deleteUser, setDeleteUser] = useState<any>();
  const [enrollTarget, setEnrollTarget] = useState<{
    subjectType: EnrollSubjectType;
    subject: any;
    groupType?: "STATIC" | "DYNAMIC";
  }>();
  const [welcomeMailUser, setWelcomeMailUser] = useState<any>();
  const [loginAsUser, setLoginAsUser] = useState<any>();
  const [deleteGroup, setDeleteGroup] = useState<any>();
  const [addUsersGroup, setAddUsersGroup] = useState<any>();

  const initiativeFilter =
    isUsersTab && selectedInitiative && selectedInitiative !== "all"
      ? selectedInitiative
      : undefined;

  const listFilters: PaginatedListFilters = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      fieldFilters: initiativeFilter
        ? { ...fieldFilters, initiative_id: initiativeFilter }
        : fieldFilters,
      sort,
    }),
    [page, search, fieldFilters, sort, initiativeFilter],
  );

  const prevInitiativeRef = useRef(selectedInitiative);
  useEffect(() => {
    if (prevInitiativeRef.current !== selectedInitiative) {
      prevInitiativeRef.current = selectedInitiative;
      if (isUsersTab && page !== 1) update({ page: 1 });
    }
  }, [selectedInitiative, isUsersTab, page, update]);

  const listUrl = useMemo(
    () => buildUGListUrl(activeTab, corporateId),
    [activeTab, corporateId],
  );

  const { items, totalItems, totalPages, isLoading, mutate } = usePaginatedList(
    listUrl,
    listFilters,
  );

  const {
    currentProcess,
    isUploadInProgress,
    progressPercentage,
    startAsyncUpload,
    downloadDiscardsCsv,
    dismissProcess,
  } = useAsyncProcess({
    processType: "USER_UPLOAD",
    corporateId,
    onCompleted: () => mutate && mutate(),
  });

  const getItemId = (item: any): string | number =>
    isUsersTab ? item.id_user : item.id;

  const handleTabChange = (next: string) => {
    setActiveTab(next as UGTabKey);
    setSelectedIds(new Set());
  };

  // TODO: selezione multipla fuori wave 1, per ripristinarla riattivare la
  // logica commentata qui sotto e il checkbox master nella BulkActionBarCst.
  const handleSelectToggle = (itemId: string | number, next: boolean) => {
    setSelectedIds(next ? new Set([itemId]) : new Set());
    // setSelectedIds((prev) => {
    //   const copy = new Set(prev);
    //   if (next) copy.add(itemId);
    //   else copy.delete(itemId);
    //   return copy;
    // });
  };

  // const handleToggleAll = (next: boolean) => {
  //   setSelectedIds(next ? new Set(items.map(getItemId)) : new Set());
  // };

  // const allChecked = items.length > 0 && selectedIds.size === items.length;
  // const someChecked = selectedIds.size > 0 && !allChecked;

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, search, fieldFilters, sort, selectedInitiative]);

  const tabs = [
    { key: "users", label: t("tabs.users") },
    { key: "static-groups", label: t("tabs.staticGroups") },
    { key: "dynamic-groups", label: t("tabs.dynamicGroups") },
  ];

  const sortOptions = [
    { label: t("sort.mostRecent"), value: "most-recent" },
    { label: t("sort.leastRecent"), value: "least-recent" },
    { label: t("sort.alphabetical"), value: "alphabetical" },
  ];

  const goToUserLO = (u: any) =>
    navigate(`/corporate/${corporateId}/detail/users/${u.id_user}/enrolled-to`);

  const goToUserEdit = (u: any) =>
    navigate(
      `/v2/corporate/${corporateId}/detail/users-groups/${u.id_user}/edit`,
    );

  // TODO: azione bulk "vedi oggetti formativi" non ancora disponibile in
  // questa wave
  const enrollNotImplemented = () => {};

  const handleBulkAddUsers = () => {
    const selectedGroup = items.find((it: any) =>
      selectedIds.has(getItemId(it)),
    );
    if (selectedGroup) setAddUsersGroup(selectedGroup);
  };

  const handleBulkEnroll = () => {
    const selectedItem = items.find((it: any) =>
      selectedIds.has(getItemId(it)),
    );
    if (!selectedItem) return;
    if (isUsersTab) {
      setEnrollTarget({ subjectType: "user", subject: selectedItem });
    } else {
      setEnrollTarget({
        subjectType: "group",
        subject: selectedItem,
        groupType: activeTab === "static-groups" ? "STATIC" : "DYNAMIC",
      });
    }
  };

  const confirmBlockUser = async () => {
    if (!blockUser) return;
    const newStatus = blockUser.status === "active" ? "SUSPENDED" : "ACTIVE";
    setIsConfirming(true);
    try {
      await fetcher(
        getQueryString(`${ServicesURL.putCustomerStatus}/${newStatus}`, {
          users_id: String(blockUser.id_user),
          corporate_id: corporateId,
        }),
        MethodType.PUT,
      );
      mutate && mutate();
      setBlockUser(undefined);
      showToast({
        severity: "success",
        message: String(t("confirm-dialog.success")),
      });
    } catch (error) {
      console.error(error);
      showToast({
        severity: "error",
        message: String(t("confirm-dialog.error")),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmBulkStatus = async () => {
    if (!bulkStatusTarget) return;
    const ids = Array.from(selectedIds);
    setIsConfirming(true);
    try {
      await Promise.all(
        ids.map((uid) =>
          fetcher(
            getQueryString(
              `${ServicesURL.putCustomerStatus}/${bulkStatusTarget}`,
              { users_id: String(uid), corporate_id: corporateId },
            ),
            MethodType.PUT,
          ),
        ),
      );
      mutate && mutate();
      setSelectedIds(new Set());
      setBulkStatusTarget(undefined);
      showToast({
        severity: "success",
        message: String(t("confirm-dialog.success")),
      });
    } catch (error) {
      console.error(error);
      showToast({
        severity: "error",
        message: String(t("confirm-dialog.error")),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteUser) return;
    setIsConfirming(true);
    try {
      await fetcher(
        getQueryString(`${ServicesURL.deleteCustomer}`, {
          users_id: String(deleteUser.id_user),
          corporate_id: corporateId,
        }),
        MethodType.DELETE,
      );
      mutate && mutate();
      setDeleteUser(undefined);
      showToast({
        severity: "success",
        message: String(t("confirm-dialog.success")),
      });
    } catch (error) {
      console.error(error);
      showToast({
        severity: "error",
        message: String(t("confirm-dialog.error")),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmWelcomeMail = async () => {
    setIsConfirming(true);
    try {
      await fetcher(
        `${ServicesURL.postWelcomemail}?corporate_id=${corporateId}`,
        MethodType.POST,
      );
      setWelcomeMailUser(undefined);
      showToast({
        severity: "success",
        message: String(t("confirm-dialog.success")),
      });
    } catch (error) {
      console.error(error);
      showToast({
        severity: "error",
        message: String(t("confirm-dialog.error")),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmLoginAs = async () => {
    if (!loginAsUser) return;
    setIsConfirming(true);
    try {
      await fetcher(
        ServicesURL.startImpersonation
          .replace("{sessionId}", sessionId)
          .replace("{corporateId}", String(loginAsUser.corporate_id))
          .replace("{externalId}", loginAsUser.external_id),
        MethodType.GET,
      );
      authUser && handleSwitchToLearner(authUser);
    } catch (error) {
      console.error(error);
      showToast({
        severity: "error",
        message: String(t("confirm-dialog.error")),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const confirmDeleteGroup = async () => {
    if (!deleteGroup) return;
    setIsConfirming(true);
    const url =
      activeTab === "static-groups"
        ? `${ServicesURL.staticGroups}/${deleteGroup.id}?id_corporate=${corporateId}`
        : `${ServicesURL.dynamicGroups.replace(
            ":idCorporate",
            corporateId,
          )}/${deleteGroup.id}`;
    try {
      await fetcher(url, MethodType.DELETE);
      mutate && mutate();
      setDeleteGroup(undefined);
      showToast({
        severity: "success",
        message: String(t("confirm-dialog.success")),
      });
    } catch (error) {
      console.error(error);
      showToast({
        severity: "error",
        message: String(t("confirm-dialog.error")),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const goToGroupLO = (group: any) => {
    const segment = activeTab === "static-groups" ? "groups" : "groups-dynamic";
    const groupType = activeTab === "static-groups" ? "STATIC" : "DYNAMIC";
    navigate(
      `/corporate/${corporateId}/detail/${segment}/${group.id}/learning-objects`,
      {
        state: { row: { ...group, groupType } },
      },
    );
  };

  const goToGroupEdit = (group: any) => {
    const segment = activeTab === "static-groups" ? "groups" : "dynamic-groups";
    navigate(
      `/v2/corporate/${corporateId}/detail/users-groups/${segment}/${group.id}/edit`,
    );
  };

  const handleDownloadCsv = async () => {
    try {
      const response = await fetcher<string>(
        `${ServicesURL.getCustomersDownloadV3}?corporate_id=${corporateId}&type=LEARNER`,
        MethodType.GET,
      );
      if (response) downloadFile(response);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownloadTemplate = () =>
    downloadFile(
      `${ServicesURL.getCustomersDownloadTemplate}?corporate_id=${corporateId}&type=LEARNER`,
    );

  const handleUploadUsersCsv = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return startAsyncUpload(formData);
  };

  const bulkActions: BulkAction[] = useMemo(() => {
    if (activeTab === "users") {
      return [
        {
          key: "enroll",
          label: t("bulk.enroll"),
          icon: <UserPlus size={14} />,
          onClick: handleBulkEnroll,
          disabled: selectedIds.size !== 1,
        },
        {
          key: "welcome-mail",
          label: t("bulk.welcomeMail"),
          icon: <Mail size={14} />,
          onClick: () => setWelcomeMailUser({}),
        },
        {
          key: "download-csv",
          label: t("bulk.downloadCsv"),
          icon: <Download size={14} />,
          onClick: handleDownloadCsv,
        },
        {
          key: "upload-users",
          label: t("bulk.uploadUsers"),
          icon: <Upload size={14} />,
          onClick: () => setUploadOpen(true),
          alwaysEnabled: true,
          disabled: isUploadInProgress,
        },
      ];
    }
    if (activeTab === "static-groups") {
      return [
        {
          key: "enroll",
          label: t("bulk.enroll"),
          icon: <UserPlus size={14} />,
          onClick: handleBulkEnroll,
          disabled: selectedIds.size !== 1,
        },
        {
          key: "add-users",
          label: t("bulk.addUsers"),
          icon: <UserRoundPlus size={14} />,
          onClick: handleBulkAddUsers,
          disabled: selectedIds.size !== 1,
        },
        {
          key: "show-lo",
          label: t("bulk.showLearningObjects"),
          icon: <GraduationCap size={14} />,
          onClick: enrollNotImplemented,
        },
      ];
    }
    return [
      {
        key: "enroll",
        label: t("bulk.enroll"),
        icon: <UserPlus size={14} />,
        onClick: handleBulkEnroll,
        disabled: selectedIds.size !== 1,
      },
      {
        key: "show-lo",
        label: t("bulk.showLearningObjects"),
        icon: <GraduationCap size={14} />,
        onClick: enrollNotImplemented,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    t,
    corporateId,
    startAsyncUpload,
    isUploadInProgress,
    selectedIds,
    items,
  ]);

  const singleSelectedId =
    selectedIds.size === 1 ? Array.from(selectedIds)[0] : null;

  const singleSelectedItem =
    singleSelectedId != null
      ? items.find((it: any) => getItemId(it) === singleSelectedId)
      : undefined;

  const selectedUsers = isUsersTab
    ? items.filter((it: any) => selectedIds.has(getItemId(it)))
    : [];
  const blockedSelectedCount = selectedUsers.filter(
    (u: any) => String(u.status).toLowerCase() === "suspended",
  ).length;
  const prevailingBlocked =
    selectedUsers.length > 0 && blockedSelectedCount * 2 > selectedUsers.length;

  const onBulkLock = () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size === 1 && singleSelectedItem) {
      setBlockUser(singleSelectedItem);
      return;
    }
    setBulkStatusTarget(prevailingBlocked ? "ACTIVE" : "SUSPENDED");
  };

  const handleBulkEdit = () => {
    if (singleSelectedId == null) return;
    if (activeTab === "users") {
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/${singleSelectedId}/edit`,
      );
    } else if (activeTab === "static-groups") {
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/groups/${singleSelectedId}/edit`,
      );
    } else {
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/dynamic-groups/${singleSelectedId}/edit`,
      );
    }
  };

  const bulkIconActions = [
    ...(singleSelectedId != null
      ? [
          {
            key: "edit",
            icon: <Pencil size={18} />,
            onClick: handleBulkEdit,
            tooltip: t("bulk.edit") || "",
          },
        ]
      : []),
    ...(isUsersTab
      ? [
          {
            key: "lock",
            icon: prevailingBlocked ? (
              <LockOpen size={18} />
            ) : (
              <Lock size={18} />
            ),
            onClick: onBulkLock,
            disabled: selectedIds.size === 0,
            tooltip:
              (prevailingBlocked ? t("bulk.unblock") : t("bulk.block")) || "",
          },
        ]
      : []),
    {
      key: "delete",
      icon: <Trash2 size={18} />,
      onClick: () => {
        if (!singleSelectedItem) return;
        if (isUsersTab) setDeleteUser(singleSelectedItem);
        else setDeleteGroup(singleSelectedItem);
      },
      disabled: !singleSelectedItem,
      tooltip: t("bulk.delete") || "",
    },
  ];

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3, pr: 3 }}>
        <Breadcrumb />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {t("title")}
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: theme.palette.text.tertiary,
                mt: 0.5,
              }}
            >
              {t("description")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Pencil size={16} />}
            onClick={() => openCreatePicker("users-groups")}
            disabled={!canCreate}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 700,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
            }}
          >
            {t("createButton")}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TabsCst
            tabs={tabs}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 240,
              "& .MuiFormControl-root": {
                minWidth: "0 !important",
                width: "100% !important",
              },
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              "& .MuiFormHelperText-root": { display: "none" },
              "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
            }}
          >
            <InputCst
              id="users-groups-search"
              label={t("searchPlaceholder") || ""}
              value={search ?? ""}
              onChange={(e: any) => update({ search: e.target.value, page: 1 })}
              endAdornment={
                <Search size={16} color={theme.palette.text.tertiary} />
              }
            />
          </Box>

          {isUsersTab ? (
            <Button
              variant="outlined"
              startIcon={<ListFilter size={16} />}
              onClick={() => setFiltersPanelOpen((prev) => !prev)}
              sx={{
                height: "40px",
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                flexShrink: 0,
                transition: "none",
                borderColor:
                  hasActiveFilters || filtersPanelOpen
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                color:
                  hasActiveFilters || filtersPanelOpen
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                backgroundColor:
                  hasActiveFilters || filtersPanelOpen
                    ? theme.palette.action.hover
                    : "transparent",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {t("filtersButton")}
            </Button>
          ) : (
            <>
              <Box
                sx={{
                  width: 200,
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  "& .MuiFormHelperText-root": { display: "none" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "8px",
                  },
                }}
              >
                <InputCst
                  id="users-groups-id-group"
                  label={t("idGroup") || ""}
                  value={(fieldFilters.id as string) ?? ""}
                  onChange={(e: any) =>
                    update({
                      fieldFilters: { ...fieldFilters, id: e.target.value },
                      page: 1,
                    })
                  }
                />
              </Box>
              <Box
                sx={{
                  width: 200,
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  "& .MuiFormHelperText-root": { display: "none" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "8px",
                  },
                }}
              >
                <InputCst
                  id="users-groups-name-group"
                  label={t("nameGroup") || ""}
                  value={(fieldFilters.name as string) ?? ""}
                  onChange={(e: any) =>
                    update({
                      fieldFilters: { ...fieldFilters, name: e.target.value },
                      page: 1,
                    })
                  }
                />
              </Box>
            </>
          )}

          <SortMenuCst
            id="users-groups-sort"
            value={sort ?? "most-recent"}
            options={sortOptions}
            onChange={(next) => update({ sort: next || null })}
          />
        </Box>

        {isUsersTab && (
          <FiltersAccordionCst
            open={filtersPanelOpen}
            onClose={() => setFiltersPanelOpen(false)}
            pageId={PagesID["corporate.customers"]}
            value={fieldFilters}
            onApply={(next) => update({ fieldFilters: next, page: 1 })}
          />
        )}

        <Box sx={{ mb: 2 }}>
          <BulkActionBarCst
            selectedCount={selectedIds.size}
            // checked={allChecked}
            // indeterminate={someChecked}
            // onToggleAll={handleToggleAll}
            selectedLabel={(count) => t("bulk.selected", { count })}
            actions={bulkActions}
            iconActions={bulkIconActions}
          />
        </Box>

        {isUsersTab && currentProcess && (
          <AsyncProcessBanner
            process={currentProcess}
            progressPercentage={progressPercentage}
            onDownloadDiscards={
              currentProcess.status ===
              AsyncProcessStatusEnum.COMPLETED_WITH_ERRORS
                ? () => downloadDiscardsCsv(currentProcess.processUuid)
                : undefined
            }
            onDismiss={dismissProcess}
          />
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
              color: theme.palette.text.tertiary,
            }}
          >
            {t("empty")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {items.map((item: any) => {
              const itemId = getItemId(item);
              const selected = selectedIds.has(itemId);
              if (isUsersTab) {
                return (
                  <UserItem
                    key={`user-${itemId}`}
                    user={item}
                    corporateId={corporateId}
                    selected={selected}
                    onSelect={(next) => handleSelectToggle(itemId, next)}
                    onEnroll={(u) =>
                      setEnrollTarget({ subjectType: "user", subject: u })
                    }
                    onViewLO={goToUserLO}
                    onWelcomeMail={(u) => setWelcomeMailUser(u)}
                    onImpersonate={(u) => setLoginAsUser(u)}
                    onEdit={goToUserEdit}
                    onBlock={(u) => setBlockUser(u)}
                    onDelete={(u) => setDeleteUser(u)}
                    onOpenDetail={(u) =>
                      navigate(
                        `/v2/corporate/${corporateId}/detail/users-groups/${u.id_user}`,
                      )
                    }
                  />
                );
              }
              return (
                <GroupItem
                  key={`group-${itemId}`}
                  group={item}
                  entityType={
                    activeTab === "static-groups"
                      ? "group-static"
                      : "group-dynamic"
                  }
                  corporateId={corporateId}
                  selected={selected}
                  onSelect={(next) => handleSelectToggle(itemId, next)}
                  onEnroll={(g) =>
                    setEnrollTarget({
                      subjectType: "group",
                      subject: g,
                      groupType:
                        activeTab === "static-groups" ? "STATIC" : "DYNAMIC",
                    })
                  }
                  onViewLO={goToGroupLO}
                  onEdit={goToGroupEdit}
                  onDelete={(g) => setDeleteGroup(g)}
                  onOpenDetail={(g) =>
                    navigate(
                      activeTab === "static-groups"
                        ? `/v2/corporate/${corporateId}/detail/users-groups/groups/${g.id}`
                        : `/v2/corporate/${corporateId}/detail/users-groups/dynamic-groups/${g.id}`,
                    )
                  }
                />
              );
            })}
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <PaginatorCst
              page={page}
              pageSize={PAGE_SIZE}
              totalItems={totalItems}
              onPageChange={(next) => update({ page: next })}
            />
          </Box>
        )}
      </Box>

      <EnrollLOModal
        open={!!enrollTarget}
        onClose={() => setEnrollTarget(undefined)}
        subjectType={enrollTarget?.subjectType ?? "user"}
        subject={enrollTarget?.subject}
        groupType={enrollTarget?.groupType}
        corporateId={corporateId}
        onSuccess={() => mutate && mutate()}
      />

      <AddUsersToGroupModal
        open={!!addUsersGroup}
        onClose={() => setAddUsersGroup(undefined)}
        corporateId={corporateId}
        group={addUsersGroup}
        onSuccess={() => mutate && mutate()}
      />

      <UploadModalCst
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title={String(t("upload-users-modal.title"))}
        accept=".csv,text/csv"
        banner={{
          title: String(t("upload-users-modal.banner-title")),
          text: String(t("upload-users-modal.banner-text")),
          downloadLabel: String(t("upload-users-modal.banner-download")),
          onDownload: handleDownloadTemplate,
        }}
        onUpload={handleUploadUsersCsv}
        successMessage={String(
          t("async-upload-started", { ns: "async-process" }),
        )}
      />

      <ConfirmDialogCst
        open={!!blockUser}
        title={
          blockUser?.status === "active"
            ? String(t("block-user.title", { ns: "modal" }))
            : String(t("unblock-user.title", { ns: "modal" }))
        }
        description={
          blockUser?.status === "active"
            ? t("block-user.description", { ns: "modal" })
            : t("unblock-user.description", { ns: "modal" })
        }
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        isSubmitting={isConfirming}
        onClose={() => setBlockUser(undefined)}
        onConfirm={confirmBlockUser}
      />

      <ConfirmDialogCst
        open={!!bulkStatusTarget}
        title={
          bulkStatusTarget === "SUSPENDED"
            ? String(t("block-user.title", { ns: "modal" }))
            : String(t("unblock-user.title", { ns: "modal" }))
        }
        description={
          bulkStatusTarget === "SUSPENDED"
            ? t("block-user.description", { ns: "modal" })
            : t("unblock-user.description", { ns: "modal" })
        }
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        isSubmitting={isConfirming}
        onClose={() => setBulkStatusTarget(undefined)}
        onConfirm={confirmBulkStatus}
      />

      <ConfirmDialogCst
        open={!!deleteUser}
        title={String(t("delete-item", { ns: "modal" }))}
        description={t("confirm-delete", { ns: "modal" })}
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        confirmVariant="danger"
        isSubmitting={isConfirming}
        onClose={() => setDeleteUser(undefined)}
        onConfirm={confirmDeleteUser}
      />

      <ConfirmDialogCst
        open={!!welcomeMailUser}
        title={String(t("confirm-welcome-mail.title", { ns: "modal" }))}
        description={t("confirm-welcome-mail.description", { ns: "modal" })}
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        isSubmitting={isConfirming}
        onClose={() => setWelcomeMailUser(undefined)}
        onConfirm={confirmWelcomeMail}
      />

      <ConfirmDialogCst
        open={!!loginAsUser}
        title={String(t("login-as-title", { ns: "modal" }))}
        description={t("login-as-description", { ns: "modal" })}
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        isSubmitting={isConfirming}
        onClose={() => setLoginAsUser(undefined)}
        onConfirm={confirmLoginAs}
      />

      <ConfirmDialogCst
        open={!!deleteGroup}
        title={String(t("delete-item", { ns: "modal" }))}
        description={t("confirm-delete", { ns: "modal" })}
        confirmLabel={String(t("confirm-dialog.confirm"))}
        cancelLabel={String(t("confirm-dialog.cancel"))}
        confirmVariant="danger"
        isSubmitting={isConfirming}
        onClose={() => setDeleteGroup(undefined)}
        onConfirm={confirmDeleteGroup}
      />
    </AnimationFadeIn>
  );
};

export default UsersGroupsV2;
